import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import '../styles/Pricing.css';

interface PricingPackage {
  id: string;
  name: string;
  writings: number;
  price: number;
  pricePerWriting: string;
  popular?: boolean;
  features: string[];
  ctaText: string;
  requiresPayment: boolean;
}

const pricingPackages: PricingPackage[] = [
  {
    id: 'free',
    name: 'Free Trial',
    writings: 1,
    price: 0,
    pricePerWriting: 'Free',
    features: [
      'Full rubric breakdown',
      'Actionable improvement advice',
      'Same quality as paid reports'
    ],
    ctaText: 'Start with Free Writing',
    requiresPayment: false
  },
  {
    id: 'pack-5',
    name: '5 Writings',
    writings: 5,
    price: 20,
    pricePerWriting: '$4 per writing',
    popular: true,
    features: [
      '$4 per writing',
      'Flexible use',
      'No expiry',
      'Best value for most families'
    ],
    ctaText: 'Buy 5 Writings',
    requiresPayment: true
  },
  {
    id: 'pack-15',
    name: '15 Writings',
    writings: 15,
    price: 50,
    pricePerWriting: '$3.33 per writing',
    features: [
      '$3.33 per writing',
      'Ideal for ongoing practice',
      'Share across different prompts',
      'Best for committed learners'
    ],
    ctaText: 'Buy 15 Writings',
    requiresPayment: true
  },
  {
    id: 'single',
    name: '1 Writing',
    writings: 1,
    price: 5,
    pricePerWriting: '$5 per writing',
    features: [
      'Same full feedback report',
      'No commitment'
    ],
    ctaText: 'Buy 1 Writing',
    requiresPayment: true
  }
];

const reportFeatures = [
  'Overall exam-style band result',
  'Rubric breakdown (Ideas, Structure, Language, Impact)',
  'Clear strengths and weaknesses',
  'Practical suggestions to move into the next band'
];

export const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { credits, claimFreeTrial, createCheckoutSession, fetchCredits } = useCredits();
  const [processingPackage, setProcessingPackage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCredits();
    }
  }, [isAuthenticated]);

  const handlePackageClick = async (pkg: PricingPackage) => {
    setError(null);
    
    // If not authenticated, redirect to signup
    if (!isAuthenticated) {
      navigate(`/signup?plan=${pkg.id}`);
      return;
    }

    // Handle free trial
    if (pkg.id === 'free') {
      if (credits?.freeTrialUsed) {
        setError('You have already used your free trial.');
        return;
      }
      
      setProcessingPackage(pkg.id);
      try {
        const success = await claimFreeTrial();
        if (success) {
          navigate('/dashboard');
        } else {
          setError('Failed to claim free trial. You may have already used it.');
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
      } finally {
        setProcessingPackage(null);
      }
      return;
    }

    // Handle paid packages - create Stripe checkout session
    setProcessingPackage(pkg.id);
    try {
      const checkoutUrl = await createCheckoutSession(pkg.id);
      if (checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = checkoutUrl;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setProcessingPackage(null);
    }
  };

  return (
    <div className="pricing-page">
      {/* Header */}
      <div className="pricing-header">
        <h1 className="pricing-title">Pricing</h1>
        <p className="pricing-subtitle">Simple, transparent pricing.</p>
        <p className="pricing-tagline">Pay only when you need feedback.</p>
        <p className="pricing-description">
          All feedback reports are based on exam-style rubrics and include clear improvement guidance.
        </p>
        
        {/* Show current balance if logged in */}
        {isAuthenticated && credits && (
          <div className="current-balance">
            <span className="balance-label">Your current balance:</span>
            <span className="balance-amount">{credits.creditBalance} credit{credits.creditBalance !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="pricing-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="pricing-grid">
        {pricingPackages.map((pkg) => (
          <div 
            key={pkg.id} 
            className={`pricing-card ${pkg.popular ? 'pricing-card--popular' : ''} ${pkg.id === 'free' ? 'pricing-card--free' : ''}`}
          >
            {pkg.popular && <span className="popular-badge">⭐ Most Popular</span>}
            
            <div className="pricing-card__header">
              <h3 className="pricing-card__name">{pkg.name}</h3>
              <div className="pricing-card__price">
                {pkg.price === 0 ? (
                  <>
                    <span className="price-amount">Free</span>
                    <span className="price-detail">1 Writing – Free</span>
                  </>
                ) : (
                  <>
                    <span className="price-amount">${pkg.price}</span>
                    <span className="price-detail">{pkg.writings} Writing{pkg.writings > 1 ? 's' : ''}</span>
                  </>
                )}
              </div>
              {pkg.id !== 'free' && (
                <p className="price-per-writing">{pkg.pricePerWriting}</p>
              )}
            </div>

            <div className="pricing-card__body">
              {pkg.id === 'free' && (
                <p className="free-description">
                  Try one complete feedback report with no cost.
                </p>
              )}
              {pkg.id === 'pack-5' && (
                <p className="package-description">
                  Perfect for short-term practice or test preparation.
                </p>
              )}
              {pkg.id === 'pack-15' && (
                <p className="package-description">
                  Designed for serious preparation and long-term improvement.
                </p>
              )}
              {pkg.id === 'single' && (
                <p className="package-description">
                  For a single extra practice or quick feedback.
                </p>
              )}

              <ul className="pricing-features">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="pricing-feature">
                    <span className="feature-check">✔</span>
                    {feature}
                  </li>
                ))}
              </ul>

              {pkg.id === 'free' && (
                <p className="no-card-note">No credit card required.</p>
              )}
            </div>

            <div className="pricing-card__footer">
              <button 
                className={`pricing-cta ${processingPackage === pkg.id ? 'processing' : ''}`}
                onClick={() => handlePackageClick(pkg)}
                disabled={processingPackage !== null || (pkg.id === 'free' && credits?.freeTrialUsed)}
              >
                {processingPackage === pkg.id ? (
                  'Processing...'
                ) : pkg.id === 'free' && credits?.freeTrialUsed ? (
                  'Already Used'
                ) : (
                  pkg.ctaText
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* What's Included Section */}
      <div className="whats-included">
        <h2 className="section-title">What's included in every report?</h2>
        <p className="section-intro">Each writing receives:</p>
        
        <div className="included-features">
          {reportFeatures.map((feature, index) => (
            <div key={index} className="included-feature">
              <span className="feature-icon">📝</span>
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div className="quality-promise">
          <p className="promise-text">
            <strong>No generic comments.</strong> <strong>No AI fluff.</strong>
          </p>
          <p className="promise-highlight">
            Only structured, exam-relevant feedback.
          </p>
        </div>
      </div>

      {/* No Subscription Banner */}
      <div className="no-subscription">
        <h2 className="no-sub-title">No subscription. No hidden fees.</h2>
        <p className="no-sub-subtitle">You only pay for what you use.</p>
        
        <div className="no-sub-features">
          <div className="no-sub-feature">
            <span className="no-sub-icon">🚫</span>
            <span>No monthly billing</span>
          </div>
          <div className="no-sub-feature">
            <span className="no-sub-icon">🚫</span>
            <span>No automatic renewal</span>
          </div>
          <div className="no-sub-feature">
            <span className="no-sub-icon">✓</span>
            <span>Your credits never expire</span>
          </div>
        </div>
      </div>

      {/* Beta Users Note */}
      <div className="beta-note">
        <h3 className="beta-title">For beta users</h3>
        <p className="beta-text">
          Selected beta users may receive complimentary writing credits for testing and feedback purposes.
        </p>
      </div>

      {/* Back to Home */}
      <div className="pricing-footer">
        <Link to="/" className="back-link">← Back to Home</Link>
      </div>
    </div>
  );
};

export default Pricing;
